/**
 * Normalize a Brazilian phone (any common format) to E.164 (+55DDDNNNNNNNN).
 * Returns null when input doesn't look like a valid BR phone.
 *
 * Accepts:
 *   "(48) 99999-8888"    →  +5548999998888
 *   "48 99999 8888"      →  +5548999998888
 *   "48999998888"        →  +5548999998888
 *   "4833334444"         →  +554833334444   (landline, 10 digits)
 *   "+5548999998888"     →  +5548999998888
 *   "5548999998888"      →  +5548999998888
 */
export function normalizeBrazilianPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');

  // With country code (12-13 digits)
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    const core = digits.slice(2);
    return isValidBrCore(core) ? `+55${core}` : null;
  }

  // Without country code (10-11 digits)
  if (isValidBrCore(digits)) {
    return `+55${digits}`;
  }

  return null;
}

// Whitelist de DDDs brasileiros válidos (Anatel · fev/2025). Bloqueia
// códigos inexistentes tipo 10, 20, 23, 30, 36, 39, 40 · também bloqueia
// DDD 55 (raro · Uruguaiana/RS · na prática o único uso comum é auto-fill
// do Chrome confundindo DDI 55 do país). Se algum dia precisar reativar 55,
// adicionar de novo aqui.
const VALID_BR_DDDS: ReadonlySet<number> = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

function isValidBrCore(digits: string): boolean {
  // 10 digits = DDD(2) + 8-digit landline
  // 11 digits = DDD(2) + 9 + 8-digit mobile (regra do 9º dígito · Anatel 2016)
  if (digits.length !== 10 && digits.length !== 11) return false;

  const ddd = parseInt(digits.slice(0, 2), 10);
  if (!VALID_BR_DDDS.has(ddd)) return false;

  // Celular precisa do 9 como 3º dígito · celular sem 9 é bug de auto-fill
  // (Chrome mascara "+55 21994-..." como DDD 55 · captura desse jeito).
  if (digits.length === 11 && digits.charAt(2) !== '9') return false;

  return true;
}

/** Returns the phone without any non-digit characters (including the "+"). */
export function phoneDigitsOnly(phoneE164: string): string {
  return phoneE164.replace(/\D/g, '');
}
